import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAccountPost } from "./AppAccountPost";
import {
    AppImportMemory, AppImportStatus, AppImportView, AppImportWatch, IAppImportHost, IAppImportStorage,
    ImportRemembered, ImportShown,
} from "./AppImportStatus";

// AppImportRun - the BROWSER half of the import status: the transport, the timers, the storage and the
// two elements the status is painted into. The rules are `AppImportStatus`' (no habitat, tested in Node).
//
// ⛔ THE LINE IS A LIVE REGION (`role=status`) AND IS WRITTEN ONLY WHEN ITS WORDS CHANGE. The elapsed
// count beside it changes every second and is `aria-hidden`, so a screen reader hears each state once.
// ⛔ TEXT ONLY (`textContent`), classes and a `data-state` word - no markup from an answer, no inline
// style (the page's Content-Security-Policy forbids it).
//
// ⚠ THE WATCH STOPS BY ITSELF WHEN ITS LINE IS NO LONGER ON THE PAGE (the card was left): the one-second
// tick looks for the element, so no route change has to remember to stop it. `Attach` - called after the
// card is painted - carries it on.

/** Who is told when what is shown changes - `AppMain`. */
export interface IAppImportOwner {
    OnImportShown: (Shown: ImportShown) => void;
}

export class AppImportPanel implements IAppImportHost {
    private Owner: IAppImportOwner;
    private Watch: AppImportWatch;
    private Memory: AppImportMemory;
    private Tick: number = 0;
    private Painted: string = "";

    public constructor(ServiceUrl: string, Owner: IAppImportOwner) {
        this.Owner = Owner;
        this.Watch = new AppImportWatch(this, AppImportPanel.UrlFor(ServiceUrl), this.OnShown);
        this.Memory = new AppImportMemory(AppImportPanel.Storage());
        document.addEventListener("visibilitychange", this.OnVisibility);
    }

    /** The `/import` address on this service, or "" when there is no service. DOCUMENTED EXCEPTION: pure. */
    public static UrlFor = (ServiceUrl: string): string => {
        let Base: string = nz(ServiceUrl).trim();
        if (Base.length === 0) { return ""; }
        while (Base.length > 0 && Base.charAt(Base.length - 1) === "/") { Base = Base.substring(0, Base.length - 1); }
        return Base + AppImportStatus.Route;
    };

    /**
     * `localStorage`, or null where the browser refuses it.
     * ERROR-SUPPRESSED-JUSTIFIED: the accessor THROWS in private browsing and under a blocked-storage
     * policy; null is the defined outcome (nothing is remembered across a reload, the page in hand works).
     */
    private static Storage = (): IAppImportStorage | null => {
        try {
            return window.localStorage;
        } catch (e) {
            return null;
        }
    };

    // ── IAppImportHost ───────────────────────────────────────────────────────────────────────────

    public Post = (Url: string, Body: string, Done: (Status: number, Text: string) => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            new AppAccountPost(Url, Body, Done).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Post");
        }
    };

    public Later = (Ms: number, Work: () => void): number => {
        // DOCUMENTED EXCEPTION: one call to the browser's timer. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return window.setTimeout(Work, Ms);
    };

    public Cancel = (Handle: number): void => {
        // DOCUMENTED EXCEPTION: one call to the browser's timer. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        window.clearTimeout(Handle);
    };

    public Now = (): number => {
        // DOCUMENTED EXCEPTION: reads the clock. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return Date.now();
    };

    public Hidden = (): boolean => {
        // DOCUMENTED EXCEPTION: reads one property. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return document.hidden === true;
    };

    // ── What `AppMain` calls ─────────────────────────────────────────────────────────────────────

    /** This visit's upload finished as `Rev`: remember it and start asking. False = nothing can be asked. */
    public Track = (Phone: string, Secret: string, Key: string, Rev: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppImportStatus.IdOk(Rev) === false) { return false; }
            const At: number = Date.now();
            if (this.Watch.Start(Phone, Secret, Key, Rev, At) === false) { return false; }
            this.Memory.Save(Phone, Rev, At);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Track");
            return false;
        }
    };

    /**
     * After a reload: ask again about the upload this browser remembers for `Phone`, with the archive
     * key (a sign-up secret does not survive a reload). False when nothing is remembered.
     */
    public Recall = (Phone: string, Key: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Watch.Watching() === true) { return true; }
            const Had: ImportRemembered | null = this.Memory.Load(Phone, Date.now());
            if (Had === null) { return false; }
            // ⚠ THE SAME UPLOAD, ALREADY SHOWN THIS VISIT (the card was left and is back): it is carried on,
            // never started again - starting again would put "waiting" over a verdict for a moment.
            if ((Had as ImportRemembered).Rev === this.Watch.Upload() && this.Shown() !== null) {
                this.Watch.Resume();
                return true;
            }
            return this.Watch.Start(Phone, "", Key, (Had as ImportRemembered).Rev, (Had as ImportRemembered).At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Recall");
            return false;
        }
    };

    /** A new upload is starting: the earlier one is no longer watched. ⚠ It stays remembered until the new one lands. */
    public Pause = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Watch.Stop();
            this.StopTick();
            this.Painted = "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Pause");
        }
    };

    /**
     * ⛔ THE ACCOUNT LEFT THIS BROWSER (`AppMain.ForgetBrowser`: closed, or its key refused): nothing is asked
     * any more and the remembered upload goes with every other trace of the account.
     */
    public Forget = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a page that has faulted must still forget.
        try {
            this.Watch.Stop();
            this.StopTick();
            this.Painted = "";
            AppImportPanel.ForgetStored(AppImportPanel.Storage());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Forget");
        }
    };

    /** Remove the remembered upload from `Store` (null = no storage). Static, so no panel has to exist. */
    public static ForgetStored = (Store: IAppImportStorage | null): void => {
        // DOCUMENTED EXCEPTION: delegates to `AppImportMemory.Clear`, which is guarded and wraps the storage call.
        new AppImportMemory(Store).Clear();
    };

    /** `ForgetStored` over this browser's own storage - what `AppMain.ForgetBrowser` calls when no panel was made. */
    public static ForgetHere = (): void => {
        // DOCUMENTED EXCEPTION: one delegation; `Storage` answers null where the browser refuses it.
        AppImportPanel.ForgetStored(AppImportPanel.Storage());
    };

    /** The card was painted: write the elapsed count, and carry the watch on if its line is back. */
    public Attach = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Line: HTMLElement | null = document.getElementById(AppImportView.LineId);
            if (Line === null) { return; }
            this.Painted = nz(Line.textContent);
            this.PaintWait();
            const Shown: ImportShown = this.Watch.Shown();
            if (Shown.Final === true || Shown.Off === true) { this.StopTick(); return; }
            this.Watch.Resume();
            if (this.Tick === 0) { this.Tick = window.setInterval(this.OnTick, 1000); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Attach");
        }
    };

    // ── Painting ─────────────────────────────────────────────────────────────────────────────────

    /** The watch has something new to show. Painted in place when the block is on the page; the owner is told. */
    private OnShown = (Shown: ImportShown): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⚠ A verdict that needs nothing more from this browser is forgotten, so a reload does not show it again.
            if (Shown.Off === true || Shown.State === AppImportStatus.Imported || Shown.State === AppImportStatus.Gone) {
                this.Memory.Clear();
            }
            this.PaintLine(Shown);
            if (Shown.Final === true || Shown.Off === true) { this.StopTick(); this.PaintWait(); }
            this.Owner.OnImportShown(Shown);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.OnShown");
        }
    };

    /** ⛔ WRITTEN ONLY WHEN THE WORDS CHANGED - the element is a live region. */
    private PaintLine = (Shown: ImportShown): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Line: HTMLElement | null = document.getElementById(AppImportView.LineId);
            const Block: HTMLElement | null = document.getElementById(AppImportView.BlockId);
            if (Line === null || Block === null) { return; }
            Block.setAttribute("data-state", AppImportView.Look(Shown));
            if (this.Painted === Shown.Sentence) { return; }
            this.Painted = Shown.Sentence;
            Line.textContent = Shown.Sentence;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.PaintLine");
        }
    };

    private PaintWait = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLElement | null = document.getElementById(AppImportView.WaitId);
            if (El === null) { return; }
            const Text: string = AppImportView.Waited(this.Watch.Shown(), Date.now());
            if (nz(El.textContent) !== Text) { El.textContent = Text; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.PaintWait");
        }
    };

    /** Once a second: the count - and, when the line has left the page, the watch stops (the card was left). */
    private OnTick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (document.getElementById(AppImportView.LineId) === null) {
                this.Watch.Stop();
                this.StopTick();
                return;
            }
            this.PaintWait();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.OnTick");
        }
    };

    private StopTick = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - it only clears a timer, and a page that has
        // faulted must still stop ticking.
        if (this.Tick !== 0) { window.clearInterval(this.Tick); this.Tick = 0; }
    };

    private OnVisibility = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (document.hidden === true) { this.Watch.OnHidden(); return; }
            if (document.getElementById(AppImportView.LineId) === null) { return; }
            this.Watch.OnVisible();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.OnVisibility");
        }
    };

    /** What is shown now, or null when nothing is being shown. */
    public Shown = (): ImportShown | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Now: ImportShown = this.Watch.Shown();
            if (nz(Now.Sentence).length === 0 || Now.Off === true) { return null; }
            return Now;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportPanel.Shown");
            return null;
        }
    };
}
